import { clsx } from 'clsx';
import { X } from 'lucide-react';
import * as React from 'react';
import { twMerge } from 'tailwind-merge';

import { Toast, ToastContext, useToast } from '../../hooks/useToast';

function cn(...inputs: Parameters<typeof clsx>) {
  return twMerge(clsx(...inputs));
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<Toast[]>([]);

  const addToast = React.useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9);
    const newToast = { ...toast, id };
    setToasts(prev => [...prev, newToast]);

    const duration = toast.duration || 5000;
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  const removeToast = React.useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastViewport />
    </ToastContext.Provider>
  );
}

function ToastViewport() {
  const { toasts, removeToast } = useToast();

  return (
    <div className="fixed top-0 right-0 z-50 flex max-h-screen w-full flex-col-reverse p-4 sm:top-auto sm:bottom-0 sm:right-0 sm:flex-col md:max-w-[420px]">
      {toasts.map(toast => (
        <ToastComponent key={toast.id} toast={toast} onClose={removeToast} />
      ))}
    </div>
  );
}

function ToastComponent({ toast, onClose }: { toast: Toast; onClose: (id: string) => void }) {
  return (
    <div
      className={cn(
        'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border border-border p-6 pr-8 shadow-lg transition-all mb-4',
        {
          'bg-background': toast.variant === 'default' || !toast.variant,
          'bg-destructive text-destructive-foreground': toast.variant === 'destructive',
          'bg-green-50 border-green-200': toast.variant === 'success',
        },
      )}>
      <div className="grid gap-1 flex-1">
        {toast.title && (
          <div
            className={cn('text-sm font-semibold', {
              'text-green-900': toast.variant === 'success',
            })}>
            {toast.title}
          </div>
        )}
        {toast.description && (
          <div
            className={cn('text-sm opacity-90', {
              'text-green-800': toast.variant === 'success',
            })}>
            {toast.description}
          </div>
        )}
      </div>
      <button
        onClick={() => onClose(toast.id)}
        className={cn(
          'absolute right-2 top-2 rounded-md p-1 opacity-0 transition-opacity hover:opacity-100 focus:opacity-100 group-hover:opacity-100',
          {
            'text-foreground/50 hover:text-foreground': toast.variant === 'default' || !toast.variant,
            'text-destructive-foreground/50 hover:text-destructive-foreground':
              toast.variant === 'destructive',
            'text-green-900/50 hover:text-green-900': toast.variant === 'success',
          },
        )}>
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
